import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAuthState } from 'contexts/auth-context';
import { useContent } from 'contexts/content-context';
import isEmpty from 'lodash/isEmpty';
import { CustomIcon, Tooltip } from '@p81-common/ui';
import { env } from 'env';
import { useRegion } from 'contexts/RegionContext';

import {
  Icon,
  Heading,
  Text,
  IconWithTooltip,
  TextWithTooltip,
  WithTooltip,
  IconCountry,
  Button,
  ButtonWithDropdown,
  ProgressBar,
  ButtonIcon
} from 'components/common/Elements/Elements';

import NetworkBody from '../NetworkBody/NetworkBody';
import NetworkBodySetting from '../NetworkBodySetting/NetworkBodySetting';
import ActualStatusMarker from '../ActualStatusMarker/ActualStatusMarker';
import BodyDropdownSetting from 'components/common/Elements/ButtonWithDropdown/parts/BodyDropdownSetting/BodyDropdownSetting';
import { getRestartOptions, getRequestBody } from './helpers/helpers';

import {
  upFirstLetter,
  chooseStatusOnGoing,
  getBeaconStatus,
  defaultRegion
} from 'utils/common';
import { showNotif } from 'utils/notification';
import { getCloudActionErrorMessage } from 'utils/cloudManagement';
import { lastVersionNetwork } from 'utils/settings';
import { links } from 'data/constants';
import { postRequest } from 'utils/requests';
import ReactJson from 'react-json-view';
import { access, resources, hasAccess } from 'utils/permissions';

import VersionSelectionModal from 'components/CS/NetworkRollout/parts/VersionSelectionModal';

const { innerIcons } = links;

import classes from './PlatformNetwork.module.scss';

const networkVersion = {
  '0.8.0': '2',
  '3.8.0': '3',
  '4.0.0': '4'
};

const PlatformNetwork = ({ item }) => {
  const { region: currentRegion } = useRegion();
  const [network, setNetwork] = useState(item);
  const [networkComponent, setNetworkComponent] = useState('false');
  const [openNetworkSetting, setOpenNetworkSetting] = useState('');
  const [idOpenNetwork, setIdOpenNetwork] = useState();
  const [jsonDataNetwork, setJsonDataNetwork] = useState({});
  const networkAccess = hasAccess([resources.CLOUD_MANAGEMENT, access.WRITE]);
  const history = useHistory();
  const { contentState } = useContent();
  const { t } = useTranslation(['activity']);
  const [action, setAction] = useState({});
  const [showProgressBar, setShowProgressBar] = useState(false);
  const { auth } = useAuthState();
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState('stable');

  let params =
    history.location.pathname.split('/').length > 6
      ? history.location.pathname.split('/')[6]
      : '';

  const regionCode = (currentRegion?.code || defaultRegion).toUpperCase();
  const lokiEnvKey = `REACT_APP_LOKI_CORE_URL_${regionCode}`;
  const lokiBaseUrl = env[lokiEnvKey];
  const grafanaUrl = lokiBaseUrl
    ? `${lokiBaseUrl}`.replace('networkId', network.networkId)
    : '';

  const grafanaBoardBaseUrl =
    env[`REACT_APP_GRAFANA_BOARD_URL_${regionCode}`] ||
    env.REACT_APP_GRAFANA_BOARD_URL_US;
  const grafanaBoardUrl = `${grafanaBoardBaseUrl}`
    .replace(
      'networkPrefix',
      (network.networkDns || '').split('.')[0].toLowerCase()
    )
    .replace(/networkId/g, network.networkId);

  useEffect(() => {
    let cancelled = false;
    async function getBeaconInfo() {
      let beaconStatus = contentState.beaconStatus.find(beacon => {
        if (beacon.networkId) return beacon.networkId == network.networkId;
      });
      if (isEmpty(beaconStatus) && network.isBeaconStatusExist) {
        const REQ_BODY = {
          customerId: network.customerId,
          networkId: network.networkId
        };
        beaconStatus = await getBeaconStatus(
          network.networkId,
          REQ_BODY,
          cancelled
        );
      }
      if (!isEmpty(beaconStatus)) {
        let newNetwork;
        if (
          beaconStatus.networkBeaconProgress == 100 ||
          ['success', 'failed'].includes(beaconStatus.networkBeaconStatus)
        ) {
          newNetwork = {
            ...network,
            isBeaconStatusExist: false,
            networkVersion: lastVersionNetwork,
            beaconStatus: false
          };
          setShowProgressBar(false);
        } else {
          newNetwork = {
            ...network,
            isBeaconStatusExist: true,
            beaconStatus: beaconStatus
          };
          setShowProgressBar(true);
        }
        setNetwork(newNetwork);
      }
    }
    getBeaconInfo();

    return () => (cancelled = true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, contentState]);

  const handleOpenNetwork = networkId => {
    if (history.location.pathname.split('/').length > 6) {
      if (history.location.pathname.split('/').slice(-2, -1)[0] !== networkId) {
        // try to open another network but one is still open
        let newUrl = history.location.pathname.split('/');
        newUrl.pop();
        newUrl.pop();
        newUrl = newUrl.join('/');
        history.replace(`${newUrl}/${networkId}/network`);
      } else {
        // close network
        let newUrl = history.location.pathname.split('/');
        newUrl.pop();
        newUrl.pop();
        newUrl = newUrl.join('/');
        history.replace(`${newUrl}`);
      }
    } else {
      let newUrl = history.location.pathname.split('/');
      newUrl = newUrl.join('/');
      history.replace(`${newUrl}/${networkId}/network`);
    }
  };

  const handleOpenNetworkSetting = networkId => {
    if (networkId !== openNetworkSetting) {
      setOpenNetworkSetting(networkId);
    } else {
      setOpenNetworkSetting('');
    }
  };
  const parsAccessTags = (data, keyField) => {
    let req = '';
    if (data && data !== '-' && data !== 'NA' && data.length > 0) {
      data.map(
        item =>
          (req += upFirstLetter(item[keyField] ? item[keyField] : item) + ', ')
      );
    } else return 'NA';
    return req.substring(0, req.length - 2);
  };

  const getDropdownList = showProgressBar => {
    return getRestartOptions(
      network,
      setAction,
      showProgressBar,
      auth.user.role,
      action.actionName,
      title,
      startAction
    );
  };

  const performStartAction = action => {
    let cancelled = false;
    const path =
      action.actionKey == 'migrate'
        ? '/cloud/management/network/migrate'
        : '/cloud/management/run-action';

    const REQ_BODY = getRequestBody(
      network.networkId,
      network.customerId,
      action
    );
    postRequest(path, REQ_BODY)
      .then(res => {
        if (!cancelled) {
          if (!isEmpty(res.body)) {
            const beaconStatus = res.body;

            const newNetworkComponent = {
              ...networkComponent,
              isBeaconStatusExist: true,
              beaconStatus: beaconStatus
            };
            setNetworkComponent(newNetworkComponent);
          }
        }
        handleOpenNetwork();
      })
      .catch(err => {
        if (!cancelled) {
          showNotif({
            message: getCloudActionErrorMessage(err),
            kind: 'error',
            dismissAfter: 8000,
            type: 'alert'
          });
        }
      });
    return () => (cancelled = true);
  };

  // Wrap startAction to handle version selection
  const startAction = action => {
    if (action && action.requiresVersion && !action.version) {
      setPendingAction(action);
      setSelectedVersion('stable');
      setVersionModalOpen(true);
      return;
    }
    return performStartAction(action);
  };

  const handleVersionProceed = (v, gradual) => {
    setSelectedVersion(v);
    setVersionModalOpen(false);
    if (pendingAction) {
      const actionWithVersion = {
        ...pendingAction,
        version: v,
        ...{ gradual }
      };
      setPendingAction(null);
      performStartAction(actionWithVersion);
    }
  };

  const handleVersionClose = () => {
    setVersionModalOpen(false);
    setPendingAction(null);
  };

  let title = network.networkId;
  useEffect(() => {
    let cancelled = false;
    idOpenNetwork &&
      postRequest('/customer/platform/network/more', {
        networkId: idOpenNetwork
      })
        .then(res => {
          !cancelled && setJsonDataNetwork(res.body);
        })
        .catch(err => {
          !cancelled && console.log(err);
        });
    return () => (cancelled = true);
  }, [idOpenNetwork]);

  const handleDetailOpen = id => {
    id !== idOpenNetwork ? setIdOpenNetwork(id) : setIdOpenNetwork('');
  };

  const getNetworkVersionIcon = netVer => {
    const version = netVer in networkVersion ? networkVersion[netVer] : '2';
    return (
      <div className={classes.attributes_icon}>
        <WithTooltip text={`Core V${version}`}>
          <div>
            <CustomIcon
              src={`${links.imagesBaseUrl}/icons2/icon_v${version}.svg`}
              color="#3a5ba2"
              className={classes.overflow_header__icon}
            />
          </div>
        </WithTooltip>
      </div>
    );
  };

  return (
    <>
      <div key={network.networkId}>
        <div
          className={cn(
            classes.cellWrap,
            classes.networkOpenMode && openNetworkSetting == network.networkId,
            classes.stickyItem
          )}
        >
          <div className={classes.cell}>
            <div className={classes.network_icon_wrap}>
              <ActualStatusMarker
                customerId={network.customerId}
                networkComponent={network}
                componentType={'network'}
              />
              {!network.networkIcon ||
              network.networkIcon ===
                '6a4f380370d56b43193171aababaf3bfa2f8002d7378b03db9207409f7734753_network_icon.png' ? (
                <Icon size="14px" icon="network" color="#3a5ba2" />
              ) : (
                <img
                  className={classes.img}
                  src={`${innerIcons}${network.networkIcon}`}
                  width="32"
                  height="32"
                  alt=""
                />
              )}
            </div>
            <div className={classes.overflow}>
              <div className={classes.nameAndAttributesWrap}>
                <Heading className={classes.overflow}>
                  <a
                    href={grafanaBoardUrl}
                    className={classes.network_label}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <TextWithTooltip
                      text={network.networkName}
                      saveNotification="activity:platform.TEXT_COPY"
                    />
                  </a>
                </Heading>
                <div className={classes.attributes_wrap}>
                  {getNetworkVersionIcon(network.networkVersion)}
                  {network.attributes?.customDNS && (
                    <div className={classes.attributes_icon}>
                      <IconWithTooltip
                        icon="customDNS"
                        color="#3a5ba2"
                        text={t('activity:platform.CUSTOM_DNS')}
                      />
                    </div>
                  )}
                  {(network.attributes?.dnsFiltering ||
                    network.attributes?.urlFiltering) && (
                    <div className={classes.attributes_icon}>
                      <IconWithTooltip
                        icon="filter"
                        color="#3a5ba2"
                        text={t('activity:platform.DNS_FILTERING')}
                        size="15px"
                      />
                    </div>
                  )}
                  {network.attributes?.splitTunneling && (
                    <div className={classes.attributes_icon}>
                      <IconWithTooltip
                        icon="split"
                        color="#3a5ba2"
                        text={t('activity:platform.SPLIT_TUNNELING')}
                        size="12px"
                      />
                    </div>
                  )}
                </div>
              </div>
              <Text className={classes.dns}>
                <TextWithTooltip
                  text={network.networkDns}
                  saveNotification="activity:platform.TEXT_COPY"
                />
              </Text>
            </div>
          </div>
          <div className={classes.cell}>
            <div className={classes.regions}>
              <Text>{t('activity:platform.REGIONS')}</Text>
              <div className={classes.regions_flags}>
                {network.regions.slice(0, 3).map(region => (
                  <div className={classes.regions_icon} key={region.regionId}>
                    <IconCountry country={region.regionCountry} size="14px" />
                  </div>
                ))}
                {network.regions.length > 3 && (
                  <Tooltip
                    title={network.regions.slice(3).map(region => (
                      <IconCountry
                        className={classes.regions_icon}
                        country={region.regionCountry}
                        size="14px"
                        key={region.regionId}
                      />
                    ))}
                  >
                    <div className={classes.regions_icon}>
                      <Text weight="b"> +{network.regions.length - 3} </Text>
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
            <div className={classes.cell}>
              <div className={cn(classes.regions, classes.overflow)}>
                <Text>
                  {network.networkVersion !== '4.0.0'
                    ? t('activity:platform.GATEWAYS')
                    : t('activity:platform.SCALE_UNITS')}
                </Text>
                <Heading>{network.gateways}</Heading>
              </div>
              <div className={classes.regions}>
                <Text>{t('activity:platform.TUNNELS')}</Text>
                <Heading>{network.tunnels.length}</Heading>
              </div>
            </div>
          </div>
          <div className={classes.cell}>
            <div className={cn(classes.grupsAndTags, classes.overflow)}>
              <Text>{t('activity:platform.FIREWALL_RULES')}</Text>
              <div className={classes.overflow}>
                <Text className={classes.text}>
                  {network.hasNtcRules ? `${network.hasNtcRules}` : 'None'}
                </Text>
              </div>
            </div>
          </div>
          <div className={classes.cell}>
            <div className={cn(classes.grupsAndTags, classes.overflow)}>
              <Text>{t('activity:platform.ACCESS_GROUPS')}</Text>
              <div className={classes.overflow}>
                <Text className={classes.text}>
                  <TextWithTooltip
                    text={parsAccessTags(network.accessGroups, 'groupName')}
                  />
                </Text>
              </div>
            </div>
          </div>
          <div className={classes.cell}>
            <div className={cn(classes.grupsAndTags, classes.overflow)}>
              <Text>{t('activity:platform.DEFAULT_ACTION')}</Text>
              <div className={classes.overflow}>
                <Text className={classes.text}>
                  <TextWithTooltip text={network.defaultAction} />
                </Text>
              </div>
            </div>
          </div>
          <div className={classes.cell}>
            <div className={cn(classes.grupsAndTags, classes.overflow)}>
              <Text>{t('activity:platform.DEFAULT_LOG')}</Text>
              <div className={classes.overflow}>
                <Text className={classes.text}>
                  <TextWithTooltip text={network.loggingPolicy} />
                </Text>
              </div>
            </div>
          </div>
          <div className={classes.cell}>
            {network.isOnGoingAlertExist && (
              <IconWithTooltip
                text={`${chooseStatusOnGoing(network.isOnGoingAlertExist)} ${t(
                  'activity:platform.MAINTENANCE'
                )}`}
                icon="icons/icon_yellow_warning.svg"
                iconFromMNX={true}
                className={classes.loki}
              />
            )}
            {network.isSetting && (
              <div className={classes.setting}>
                <Icon
                  size={'24px'}
                  icon="settingsEq"
                  color="#3a5ba2"
                  onClick={() => handleOpenNetworkSetting(network.networkId)}
                  className={
                    openNetworkSetting !== network.networkId
                      ? ''
                      : classes.iconUp
                  }
                />
              </div>
            )}
            <a
              href={`${grafanaUrl}`}
              className={classes.loki}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Icon size={'32px'} icon="loki" />
            </a>
            {networkAccess && (
              <ButtonWithDropdown
                tooltipDisabled
                icon="3dots"
                degRotateImg={90}
                position={'left'}
                color={showProgressBar ? '#b3bac8' : '#536283'}
                closeOnChange={true}
                className={classes.pointsWrap}
                buttonClass={classes.points}
              >
                <BodyDropdownSetting items={getDropdownList(showProgressBar)} />
              </ButtonWithDropdown>
            )}
            <div className={classes.arrowWrap}>
              <Button onClick={() => handleOpenNetwork(network.networkId)}>
                <Icon
                  size={'32px'}
                  icon="arrow"
                  color={network.isBeaconStatusExist ? '#CBD2E2' : '#3a5ba2'}
                  className={params !== network.networkId ? '' : classes.iconUp}
                />
              </Button>
            </div>
          </div>
        </div>

        {params === network.networkId && (
          <NetworkBody
            selectId={network.networkId}
            hasNtcRules={network.hasNtcRules}
            customerId={network.customerId}
            setShowProgressBar={setShowProgressBar}
            isGrafBlocked={showProgressBar}
          />
        )}
        {showProgressBar && params === network.networkId && (
          <div className={classes.progressBarWraper}>
            <ProgressBar
              is={network.beaconStatus?.networkBeaconProgress || 0}
              of={100}
              text={
                'Running ' +
                t(
                  `activity:cm.ACTION-${
                    action.actionKey
                      ? action.actionKey.toUpperCase()
                      : !isEmpty(network.beaconStatus) &&
                        network.beaconStatus.action
                      ? network.beaconStatus.action.toUpperCase()
                      : 'DEFAULT'
                  }`
                )
              }
              progressClass={classes.progressBar}
              positionCenter
            />
          </div>
        )}
        {params === network.networkId && (
          <div className={classes.jsonWrap}>
            {idOpenNetwork === network.networkId && (
              <div className={classes.jsonInfoWraper}>
                <ReactJson
                  src={jsonDataNetwork}
                  iconStyle="triangle"
                  enableClipboard={false}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  indentWidth={4}
                  collapsed={false}
                />
              </div>
            )}
            {!idOpenNetwork ? (
              <ButtonIcon
                icon="close"
                buttonText={t('activity:buttons.MORE_DETAILS')}
                rotateIcon={45}
                onClick={() => handleDetailOpen(network.networkId)}
                marginLeftAuto={true}
              />
            ) : (
              <ButtonIcon
                icon="close"
                buttonText={t('activity:buttons.CLOSE_DETAILS')}
                onClick={() => handleDetailOpen(network.networkId)}
                marginLeftAuto={true}
              />
            )}
          </div>
        )}
        {openNetworkSetting === network.networkId && (
          <NetworkBodySetting selectId={network.networkId} />
        )}
      </div>
      <VersionSelectionModal
        action={
          pendingAction
            ? pendingAction.actionKey === 'network-automatic-update'
              ? 'network-automatic-update'
              : 'force-network-upgrade'
            : null
        }
        open={versionModalOpen}
        initialVersion={selectedVersion}
        onClose={handleVersionClose}
        onProceed={handleVersionProceed}
      />
    </>
  );
};

PlatformNetwork.propTypes = {
  item: PropTypes.object.isRequired
};

export default PlatformNetwork;
